// HQ Live (spec/14 §2; F-ANL-02, F-HQ-01's first slice): the Platform right now — players in session, stations in use
// of the total, venues and organisations online, takings today (M-01 across every venue's own business day) and new
// players today — then the venues offline for more than ten minutes during their opening hours, and the busiest now.
//
// Aggregate only: HQ reads the worker's read model, never an organisation's rows. While the read model is being
// rebuilt (after a Valkey flush) every figure is "—", not a zero.

import type { HqLive } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { InfoTip, StatCard, Table, type TableColumn } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { formatMoney } from '../../org/sessions/money';
import { LoadFailure } from '../../shell/LoadFailure';
import { Notice, Page, Section, SummaryMeter, SummaryStrip, TableSkeleton } from '../../ui';
import { TickingMoney, TickingNumber } from '../../ui/ticking';
import { RequirePlatformPermission } from '../access';
import { HQ_ORGANIZATIONS } from '../sections';
import { useHqLive } from './queries';


type LiveVenue = HqLive['offlineVenues'][number];

const NO_DATA = '—';

export function LivePage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_ORGANIZATIONS}>
      <Live />
    </RequirePlatformPermission>
  );
}

function Live(): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const live = useHqLive();
  // A count ticks to its new value when the read model moves on; "—" while it is being rebuilt.
  const count = (value: number | null) =>
    value === null ? <span className="console-num">{NO_DATA}</span> : <TickingNumber className="console-num" value={value} format={(n) => format.number(n)} />;

  let body: ReactNode;
  if (live.data === undefined) {
    body = live.isError ? <LoadFailure error={live.error} message={t('live.failed')} onRetry={() => void live.refetch()} /> : <TableSkeleton />;
  } else {
    const data = live.data;
    // One currency ticks like the floor's takings; several read as a sum of each, as they are.
    const single = data.takingsToday.length === 1 ? data.takingsToday[0] : undefined;
    const takings =
      single !== undefined ? (
        <TickingMoney className="console-num" value={single} format={(money) => formatMoney(money)} />
      ) : (
        <span className="console-num">{data.takingsToday.length === 0 ? NO_DATA : data.takingsToday.map((money) => formatMoney(money)).join(' + ')}</span>
      );
    const inUse = data.stationsInUse === null || data.stationsTotal === null || data.stationsTotal === 0 ? null : data.stationsInUse / data.stationsTotal;
    body = (
      <>
        {data.computedAt === null ? <Notice tone="info">{t('live.rebuilding')}</Notice> : null}
        {/* The Platform's figures as the Floor's summary strip (ADR-0043): what is live carries the ember rail, how full
            the stations are a meter, and every figure ticks to its new value. */}
        <SummaryStrip label={t('live.title')}>
          <StatCard className="console-summary__cell--live" label={t('live.playersInSession')} value={count(data.playersInSession)} />
          <StatCard
            label={t('live.stationsInUse')}
            value={count(data.stationsInUse)}
            unit={data.stationsTotal === null ? undefined : t('live.ofTotal', { total: format.number(data.stationsTotal) })}
            {...(inUse === null
              ? {}
              : {
                  hint: (
                    <>
                      <SummaryMeter fill={inUse} />
                      {t('live.inUse', { percent: Math.round(inUse * 100) })}
                    </>
                  ),
                })}
          />
          <StatCard
            label={t('live.venuesOnline')}
            value={count(data.venuesOnline)}
            unit={data.venuesTotal === null ? undefined : t('live.ofTotal', { total: format.number(data.venuesTotal) })}
            hint={data.organizationsOnline === null ? undefined : t('live.organizationsOnline', { online: data.organizationsOnline, total: data.organizationsTotal ?? 0 })}
          />
          <StatCard label={t('live.takingsToday')} value={takings} hint={t('live.takingsHint')} />
          <StatCard label={t('live.newPlayersToday')} value={count(data.newPlayersToday)} />
        </SummaryStrip>
        <Section title={t('live.offline')} description={t('live.offlineHint')} body="rows">
          <VenuesTable venues={data.offlineVenues} empty={t('live.noneOffline')} caption={t('live.offline')} offline />
        </Section>
        <Section title={t('live.busiest')} body="rows">
          <VenuesTable venues={data.busiestVenues} empty={t('live.noneBusy')} caption={t('live.busiest')} offline={false} />
        </Section>
        {data.computedAt === null ? null : <p className="console-subtle">{t('live.computedAt', { at: format.dateTime(data.computedAt, { date: 'medium', time: 'short' }) })}</p>}
      </>
    );
  }

  return (
    <Page title={t('live.title')} meta={<InfoTip>{t('live.intro')}</InfoTip>}>
      {body}
    </Page>
  );
}

function VenuesTable({ venues, empty, caption, offline }: { readonly venues: readonly LiveVenue[]; readonly empty: string; readonly caption: string; readonly offline: boolean }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const columns: readonly TableColumn<LiveVenue>[] = [
    { key: 'venue', header: t('live.column.venue'), cell: (row) => row.name },
    { key: 'sessions', header: t('live.column.sessions'), numeric: true, cell: (row) => <span className="console-num">{format.number(row.sessionsNow)}</span> },
    {
      key: 'stations',
      header: t('live.column.stations'),
      numeric: true,
      cell: (row) => <span className="console-num">{t('live.stationsOnline', { online: row.stationsOnline, total: row.stationsTotal })}</span>,
    },
    ...(offline
      ? [{ key: 'since', header: t('live.column.offlineSince'), cell: (row: LiveVenue) => (row.offlineSince === null ? NO_DATA : format.dateTime(row.offlineSince, { date: 'medium', time: 'short' })) }]
      : []),
  ];
  return (
    <div className="console-table-card">
      {/* A venue offline in its opening hours carries the danger rail; a busy one the live rail, as on the floor. */}
      <Table caption={caption} captionHidden columns={columns} rows={venues} rowKey={(row) => row.venueId} rowTone={() => (offline ? 'danger' : 'live')} empty={empty} />
    </div>
  );
}
